import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spin, Empty, Tooltip } from 'antd';
import { 
  VerticalAlignTopOutlined,
  FileTextOutlined,
  DownOutlined,
  RightOutlined,
  MenuOutlined
} from '@ant-design/icons';
import './UserManualPage.css';
import apiClient from '../utils/api';

// 使用手册数据接口
interface ManualItem {
  manual_id: number;
  title: string;
  content: string;  // HTML内容
  parent_id: number;
  sort: number;
  children?: ManualItem[];
}

// API响应接口
interface ListApiResponse {
  code: number;
  msg: string;
  data: {
    tree: ManualItem[];
  };
}

interface DetailApiResponse {
  code: number;
  msg: string;
  data: {
    detail: ManualItem;
  };
}

const UserManualPage: React.FC = () => {
  // 状态管理
  const [manualList, setManualList] = useState<ManualItem[]>([]);
  const [currentItem, setCurrentItem] = useState<ManualItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set());
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState<boolean>(false);
  
  const contentRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  // 获取使用手册列表
  const fetchManualList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<ListApiResponse>('/index.php?s=/api/usermanual/list');
      if (response.data.code === 1) {
        // 后端返回的是 { tree: [...] } 结构
        const list = response.data.data.tree || [];
        setManualList(list);
        
        // 如果有数据，默认选中第一个
        if (list.length > 0) {
          const firstItem = findFirstLeafItem(list);
          if (firstItem) {
            await fetchManualDetail(firstItem.manual_id);
            // 展开包含当前选中项的所有父级
            expandParents(list, firstItem.manual_id);
          }
        }
      }
    } catch (error) {
      console.error('获取使用手册列表失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取使用手册详情
  const fetchManualDetail = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const response = await apiClient.get<DetailApiResponse>(`/index.php?s=/api/usermanual/detail&id=${id}`);
      if (response.data.code === 1) {
        // 后端返回的是 { detail: {...} } 结构
        const item = response.data.data.detail;
        setCurrentItem(item);

      }
    } catch (error) {
      console.error('获取使用手册详情失败:', error);
    } finally {
      setLoading(false);
    }
  }, [manualList]);

  // 找到第一个叶子节点
  const findFirstLeafItem = (list: ManualItem[]): ManualItem | null => {
    for (const item of list) {
      if (!item.children || item.children.length === 0) {
        return item;
      }
      const child = findFirstLeafItem(item.children);
      if (child) return child;
    }
    return null;
  };

  // 展开父级节点
  const expandParents = (list: ManualItem[], targetId: number): boolean => {
    for (const item of list) {
      if (item.manual_id === targetId) {
        return true;
      }
      if (item.children && item.children.length > 0) {
        const found = expandParents(item.children, targetId);
        if (found) {
          setExpandedKeys(prev => new Set(Array.from(prev).concat(item.manual_id)));
          return true;
        }
      }
    }
    return false;
  };

  // 切换章节
  const handleItemClick = (item: ManualItem) => {
    if (item.children && item.children.length > 0) {
      // 如果有子节点，展开/折叠
      toggleExpand(item.manual_id);
    } else {
      // 如果是叶子节点，加载内容
      fetchManualDetail(item.manual_id);
      // 移动端自动关闭侧边栏
      if (window.innerWidth <= 768) {
        setMobileSidebarVisible(false);
      }
    }
  };

  // 展开/折叠节点
  const toggleExpand = (id: number) => {
    setExpandedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // 返回顶部 - 滚动到 page-content-wrapper 的顶部
  const scrollToTop = () => {
    // 1. 首先滚动外层页面到顶部（Home 组件中的 page-content-wrapper）
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // 2. 同时滚动 manual-content 容器内部到 content-wrapper 顶部
    if (contentRef.current) {
      if (contentWrapperRef.current) {
        const wrapperTop = contentWrapperRef.current.offsetTop;
        contentRef.current.scrollTo({
          top: wrapperTop,
          behavior: 'smooth'
        });
      } else {
        contentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  // 处理图片点击放大
  const handleImageClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const imgSrc = target.getAttribute('src');
      if (imgSrc) {
        setZoomedImage(imgSrc);
      }
    }
  }, []);

  // 关闭图片放大
  const closeZoom = useCallback(() => {
    setZoomedImage(null);
  }, []);

  // 递归渲染目录树
  const renderTree = (items: ManualItem[], level: number = 0): React.ReactNode => {
    return items.map(item => {
      const isExpanded = expandedKeys.has(item.manual_id);
      const isActive = currentItem?.manual_id === item.manual_id;
      const hasChildren = item.children && item.children.length > 0;
      
      return (
        <div key={item.manual_id} className="tree-item-wrapper">
          <div
            className={`tree-item ${isActive ? 'active' : ''} level-${level}`}
            style={{ paddingLeft: `${16 + level * 16}px` }}
            onClick={() => handleItemClick(item)}
          >
            {hasChildren && (
              <span className="tree-expand-icon" onClick={(e) => {
                e.stopPropagation();
                toggleExpand(item.manual_id);
              }}>
                {isExpanded ? <DownOutlined /> : <RightOutlined />}
              </span>
            )}
            {!hasChildren && <span className="tree-icon-placeholder" />}
            <FileTextOutlined className="tree-item-icon" />
            <span className="tree-item-title">{item.title}</span>
          </div>
          {hasChildren && isExpanded && (
            <div className="tree-children">
              {renderTree(item.children!, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // 初始化加载
  useEffect(() => {
    fetchManualList();
  }, [fetchManualList]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileSidebarVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="user-manual-page">
      {/* 移动端顶部导航栏 */}
      <header className="mobile-header">
        <Button
          type="text"
          icon={<MenuOutlined />}
          className="mobile-header-menu-btn"
          onClick={() => setMobileSidebarVisible(true)}
        />
        <span className="mobile-header-title">
          {currentItem?.title || '使用手册'}
        </span>
        <div className="mobile-header-placeholder" />
      </header>

      {/* 页面主体 */}
      <div className="manual-container">
        {/* 左侧侧边栏 */}
        <aside className={`manual-sidebar ${mobileSidebarVisible ? 'mobile-visible' : ''}`}>
          <div className="sidebar-header">
            <span className="sidebar-title">目录</span>
            <Button
              type="text"
              size="small"
              onClick={() => setMobileSidebarVisible(false)}
              className="sidebar-close-btn"
            >
              关闭
            </Button>
          </div>
          <div className="sidebar-content">
            {manualList.length > 0 ? (
              renderTree(manualList)
            ) : loading ? (
              <div className="sidebar-loading">
                <Spin size="small" />
                <span>加载中...</span>
              </div>
            ) : (
              <Empty description="暂无目录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        </aside>

        {/* 移动端遮罩层 */}
        {mobileSidebarVisible && (
          <div 
            className="mobile-overlay" 
            onClick={() => setMobileSidebarVisible(false)}
          />
        )}

        {/* 右侧内容区域 */}
        <main className="manual-content" ref={contentRef}>
          {loading ? (
            <div className="content-loading">
              <Spin size="large" />
              <p>加载中...</p>
            </div>
          ) : currentItem ? (
            <div className="content-wrapper" ref={contentWrapperRef}>
              <h1 className="content-title">{currentItem.title}</h1>
              <div 
                className="content-body"
                dangerouslySetInnerHTML={{ __html: currentItem.content }}
                onClick={handleImageClick}
              />
            </div>
          ) : (
            <Empty 
              description="请选择左侧章节查看内容" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}

          {/* 移动端目录按钮 */}
          <Tooltip title="打开目录" placement="left">
            <Button
              type="primary"
              shape="circle"
              icon={<MenuOutlined />}
              className="mobile-menu-float-btn"
              onClick={() => setMobileSidebarVisible(true)}
            />
          </Tooltip>

          {/* 返回顶部按钮 */}
          <Tooltip title="返回顶部" placement="left">
            <Button
              type="primary"
              shape="circle"
              icon={<VerticalAlignTopOutlined />}
              className="back-to-top"
              onClick={scrollToTop}
            />
          </Tooltip>

          {/* 图片放大查看 */}
          {zoomedImage && (
            <>
              <div className="img-zoom-overlay" onClick={closeZoom} />
              <img 
                src={zoomedImage} 
                alt="放大查看" 
                className="img-zoomed"
                onClick={closeZoom}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default UserManualPage;
